import type { Variant } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useVariants } from '@/state/experiments/useVariants';
import { apiErrorMessage } from '@/state/api-error';

type DuplicateVariantModalProps = {
  variant: Variant;
  experimentId: number;
  open: boolean;
  onClose: () => void;
};

export const DuplicateVariantModal = ({ variant, experimentId, open, onClose }: DuplicateVariantModalProps) => {
  const { duplicateVariant } = useVariants(experimentId);
  const [name, setName] = useState(`${variant.name} (copy)`);
  const [isSaving, setIsSaving] = useState(false);

  const canSave = name.trim().length > 0;

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!canSave || isSaving) return;
    setIsSaving(true);
    try {
      await duplicateVariant(variant.id, name);
      toast.success(`Created "${name.trim()}".`);
      onClose();
    } catch (error) {
      // Stays open with the typed name intact, so a failure is never mistaken for a click
      // that did nothing. apiErrorMessage, not instanceof Error: the API client throws a
      // plain object, and a 400 (reserved name, not a draft) reaches no other dialog.
      toast.error(apiErrorMessage(error, 'Could not duplicate the variant.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="border-line bg-panel sm:rounded-panel sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-title font-medium">Duplicate Variant</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4 py-2">
          <p className="supporting">The copy carries over all of the changes made in "{variant.name}".</p>
          <div className="grid gap-2">
            <Label htmlFor="duplicate-variant-name" className="micro-label">
              Name of the new variant
            </Label>
            <Input
              id="duplicate-variant-name"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              className="border-line bg-field text-foreground rounded-lg"
              autoFocus
            />
            {!canSave && <span className="supporting">Enter a name for the copy to continue.</span>}
          </div>
        </form>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSave || isSaving}
            className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
          >
            {isSaving ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
